// iOS: keeps call audio alive while the app is in the background.
//
// On iOS the call's audio (mic capture and remote playback) doesn't run in our own process but in
// WebKit's GPU process, which picks its *own* AVAudioSession category from what the page is doing.
// A call with the mic muted has no audio capture, only Web Audio playback (`webAudioMix`), and for
// that WebKit chooses `AmbientSound` — a category iOS never lets play in the background, whatever
// `UIBackgroundModes: audio` says. Captured on a real device: the moment the app was backgrounded,
// audiomxd paused the GPU process's session ("Pause process 'com.apple.WebKit(…)'"), so the call
// went silent and the app lost its reason to stay alive. `BackgroundAudioBootstrap.mm` can't help
// here: it sets `PlayAndRecord` on the app process's session, not on WebKit's.
//
// WebKit's Audio Session API (`navigator.audioSession`, iOS 16.4+) is how a page tells WebKit which
// category to use: `play-and-record` for the whole call, back to `auto` once it ends.
//
// iOS only. Android's WebView has no such API (the guard below makes it a no-op there anyway), and
// on macOS WebKit exposes it but the desktop app has no background restriction to work around, so
// it's left at WebKit's default rather than changing audio routing there for no benefit.

type AudioSessionType = 'auto' | 'playback' | 'transient' | 'transient-solo' | 'ambient' | 'play-and-record'

interface NavigatorWithAudioSession {
  audioSession?: { type: AudioSessionType }
}

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)

function audioSession() {
  if (!isIOS) return null
  return (navigator as unknown as NavigatorWithAudioSession).audioSession ?? null
}

/** Called when a call starts (`true`) and whenever call state is reset on any disconnect path (`false`). */
export function setCallAudioSessionActive(active: boolean) {
  const session = audioSession()
  if (!session) return
  const type: AudioSessionType = active ? 'play-and-record' : 'auto'
  if (session.type === type) return
  try {
    session.type = type
  } catch (error) {
    console.warn('[audioSession] Failed to set navigator.audioSession.type', type, error)
  }
}
